import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiTrash2, FiCheckCircle } from 'react-icons/fi';
import {
  submitEntry, getUserCompanyCodes,
  getCostCenters, validateCostCenter,
  getProfitCenters, validateProfitCenter,
  getWbsElements,
} from '../../utils/journalApi';

const genUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
};

const emptyLine = () => ({
  id: genUUID(), posting_key: '', gl_account: '', amount: '',
  cost_center: '', profit_center: '', wbs_element: '', item_text: '',
});

const validateHeader = (h) => {
  const e = {};
  const allowedCodes = getUserCompanyCodes();
  if (!h.company_code) e.company_code = 'Required';
  else if (h.company_code.length !== 4) e.company_code = 'Must be 4 characters';
  else if (allowedCodes.length && !allowedCodes.includes(h.company_code)) e.company_code = 'Not in your permitted company codes';
  if (!h.document_date) e.document_date = 'Required';
  if (!h.posting_date)  e.posting_date  = 'Required';
  if (!h.document_type) e.document_type = 'Required';
  else if (h.document_type.length !== 2) e.document_type = 'Must be 2 characters';
  if (!h.currency) e.currency = 'Required';
  else if (h.currency.length !== 3) e.currency = 'Must be 3 characters';
  if (h.reference?.length  > 16) e.reference  = 'Max 16 chars';
  if (h.header_text?.length > 25) e.header_text = 'Max 25 chars';
  return e;
};

const validateLines = (lines) => {
  const e = {};
  lines.forEach(l => {
    if (!l.posting_key || !['40','50'].includes(l.posting_key)) e[`${l.id}.posting_key`] = 'Required';
    if (!l.gl_account) e[`${l.id}.gl_account`] = 'Required';
    else if (l.gl_account.length > 10) e[`${l.id}.gl_account`] = 'Max 10';
    if (!l.amount || Number(l.amount) <= 0) e[`${l.id}.amount`] = '> 0 required';
  });
  return e;
};

export default function JournalEntryNew({ onNavigate }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(null);
  const [header, setHeader] = useState({
    company_code: '', document_date: '', posting_date: '',
    document_type: '', currency: '', reference: '', header_text: '',
  });
  const [lines, setLines] = useState([emptyLine(), emptyLine()]);
  const [hErr, setHErr] = useState({});
  const [lErr, setLErr] = useState({});

  // Master data
  const [masterLoading, setMasterLoading] = useState(false);
  const [costCenters,   setCostCenters]   = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [wbsElements,   setWbsElements]   = useState([]);
  const [wbsDisabled,   setWbsDisabled]   = useState(false);
  // Per-line validation: { [`${id}.cost_center`]: {state:'ok'|'error', msg} }
  const [lineVal, setLineVal]   = useState({});
  const validatingRef = useRef({});

  /* ── Load master data when company_code reaches 4 chars ── */
  useEffect(() => {
    if (header.company_code.length === 4) {
      loadMasterData(header.company_code);
    } else {
      setCostCenters([]); setProfitCenters([]);
      setWbsElements([]); setWbsDisabled(false);
      setLineVal({});
    }
  }, [header.company_code]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMasterData = async (cc) => {
    setMasterLoading(true);
    try {
      const [ccRes, pcRes, wbsRes] = await Promise.all([
        getCostCenters(cc), getProfitCenters(cc), getWbsElements(cc),
      ]);
      setCostCenters(ccRes.data   || []);
      setProfitCenters(pcRes.data || []);
      const wbs = wbsRes.data || [];
      setWbsElements(wbs);
      setWbsDisabled(wbs.length === 0);
    } catch (_) {}
    setMasterLoading(false);
  };

  const setH = (k, v) => { setHeader(h => ({ ...h, [k]: v })); setHErr(e => ({ ...e, [k]: '' })); };
  const setL = (id, k, v) => {
    setLines(ls => ls.map(l => l.id === id ? { ...l, [k]: v } : l));
    setLErr(e => ({ ...e, [`${id}.${k}`]: '' }));
  };

  const addLine    = () => setLines(ls => [...ls, emptyLine()]);
  const removeLine = (id) => {
    if (lines.length <= 2) { toast.warn('Minimum 2 line items required.'); return; }
    setLines(ls => ls.filter(l => l.id !== id));
    setLineVal(v => { const n = { ...v }; delete n[`${id}.cost_center`]; delete n[`${id}.profit_center`]; return n; });
  };

  /* ── Cost Centre ── */
  const handleCCChange = (lineId, value) => {
    setL(lineId, 'cost_center', value);
    const match = costCenters.find(c => c.cost_center === value);
    if (match) {
      if (match.profit_center) setL(lineId, 'profit_center', match.profit_center);
      setLineVal(v => ({ ...v, [`${lineId}.cost_center`]: { state: 'ok', msg: '' } }));
    } else {
      setLineVal(v => ({ ...v, [`${lineId}.cost_center`]: null }));
    }
  };

  const handleCCBlur = async (lineId, value) => {
    if (!value || !header.company_code) return;
    const key = `${lineId}.cost_center`;
    if (validatingRef.current[key]) return;
    const match = costCenters.find(c => c.cost_center === value);
    if (match) { setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } })); return; }
    validatingRef.current[key] = true;
    try {
      const res = await validateCostCenter(value, header.company_code);
      setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } }));
      if (res.data?.profit_center) setL(lineId, 'profit_center', res.data.profit_center);
    } catch (err) {
      const s = err.response?.status;
      const msg = s === 404 ? 'Cost centre not found' : s === 422 ? 'Cost centre is blocked or expired' : '';
      setLineVal(v => ({ ...v, [key]: { state: 'error', msg } }));
    } finally { validatingRef.current[key] = false; }
  };

  /* ── Profit Centre ── */
  const handlePCChange = (lineId, value) => {
    setL(lineId, 'profit_center', value);
    const match = profitCenters.find(p => p.profit_center === value);
    if (match) setLineVal(v => ({ ...v, [`${lineId}.profit_center`]: { state: 'ok', msg: '' } }));
    else        setLineVal(v => ({ ...v, [`${lineId}.profit_center`]: null }));
  };

  const handlePCBlur = async (lineId, value) => {
    if (!value || !header.company_code) return;
    const key = `${lineId}.profit_center`;
    if (validatingRef.current[key]) return;
    const match = profitCenters.find(p => p.profit_center === value);
    if (match) { setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } })); return; }
    validatingRef.current[key] = true;
    try {
      await validateProfitCenter(value, header.company_code);
      setLineVal(v => ({ ...v, [key]: { state: 'ok', msg: '' } }));
    } catch (err) {
      const s = err.response?.status;
      const msg = s === 404 ? 'Profit centre not found' : s === 422 ? 'Profit centre is blocked or expired' : '';
      setLineVal(v => ({ ...v, [key]: { state: 'error', msg } }));
    } finally { validatingRef.current[key] = false; }
  };

  /* ── Computed ── */
  const debits   = lines.filter(l => l.posting_key === '40').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const credits  = lines.filter(l => l.posting_key === '50').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const balanced = debits > 0 && credits > 0 && Math.abs(debits - credits) < 0.001;
  const hasValErrors = Object.values(lineVal).some(v => v?.state === 'error');

  /* ── Submit ── */
  const handleSubmit = async () => {
    const he = validateHeader(header);
    const le = validateLines(lines);
    setHErr(he); setLErr(le);
    if (Object.keys(he).length || Object.keys(le).length) { toast.error('Please fix errors.'); return; }
    if (!balanced)    { toast.error('Debits must equal credits.'); return; }
    if (hasValErrors) { toast.error('Fix cost/profit centre validation errors.'); return; }

    setSubmitting(true);
    try {
      const res = await submitEntry({
        request_id:  genUUID(),
        ...header,
        reference:   header.reference   || undefined,
        header_text: header.header_text || undefined,
        line_items: lines.map(({ id, ...r }, idx) => ({
          line_item_id:  idx + 1,
          posting_key:   r.posting_key,
          gl_account:    r.gl_account,
          amount:        String(Number(r.amount).toFixed(2)),
          cost_center:   r.cost_center   || undefined,
          profit_center: r.profit_center || undefined,
          wbs_element:   r.wbs_element   || undefined,
          item_text:     r.item_text     || undefined,
        })),
        attachments: [],
      });
      toast.success('Entry submitted successfully.');
      setSubmitted(res.data);
    } catch (err) {
      const s = err.response?.status;
      if (s === 409) toast.error('Duplicate request ID — please try again.');
      else if (s === 400) { const d = err.response.data; if (d?.message) toast.error(d.message); }
      else if (s === 422) { setHErr(p => ({ ...p, ...(err.response.data?.detail || {}) })); toast.error('Fix the highlighted errors.'); }
    }
    setSubmitting(false);
  };

  /* ── Success screen ── */
  if (submitted) {
    return (
      <div className="journal-panel" style={{ textAlign: 'center', padding: '48px 32px' }}>
        <FiCheckCircle size={52} style={{ color: '#22c55e', marginBottom: 16 }} />
        <h2 style={{ margin: '0 0 8px', color: '#12213a' }}>Entry Submitted!</h2>
        <p className="journal-muted" style={{ marginBottom: 4 }}>
          Tracking ID: <code style={{ fontFamily: 'monospace', fontWeight: 700, color: '#5b5fc7' }}>{submitted.tracking_id}</code>
        </p>
        <p className="journal-muted" style={{ marginBottom: 24 }}>Status: <strong>{submitted.status}</strong></p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="journal-btn primary" onClick={() => onNavigate('entries')}>View Entries</button>
          <button className="journal-btn ghost" onClick={() => {
            setSubmitted(null);
            setHeader({ company_code: '', document_date: '', posting_date: '', document_type: '', currency: '', reference: '', header_text: '' });
            setLines([emptyLine(), emptyLine()]);
            setLineVal({});
          }}>Submit Another</button>
        </div>
      </div>
    );
  }

  const field = (key, label, required, type = 'text', extra = {}) => (
    <div className="journal-field" key={key}>
      <label>{label}{required ? ' *' : ''}</label>
      <input type={type} value={header[key]} onChange={e => setH(key, e.target.value)}
        className={hErr[key] ? 'err' : ''} {...extra} />
      {hErr[key] && <span className="journal-field-err">{hErr[key]}</span>}
    </div>
  );

  const ccBorder = (id) => lineVal[`${id}.cost_center`]?.state === 'ok'    ? '#22c55e'
                         : lineVal[`${id}.cost_center`]?.state === 'error'  ? '#ef4444' : undefined;
  const pcBorder = (id) => lineVal[`${id}.profit_center`]?.state === 'ok'   ? '#22c55e'
                         : lineVal[`${id}.profit_center`]?.state === 'error' ? '#ef4444' : undefined;

  const spinner = masterLoading
    ? <span style={{ marginLeft: 4, display: 'inline-block', verticalAlign: 'middle' }}>
        <div className="journal-spinner-sm" style={{ display: 'inline-block' }} />
      </span>
    : null;

  return (
    <div>
      {/* Header */}
      <div className="journal-panel journal-form-section">
        <div className="journal-panel-head"><h3>Header Information</h3></div>
        <div className="journal-field-grid">
          {field('company_code',  'Company Code',  true,  'text', { maxLength: 4,  placeholder: 'e.g. 1000' })}
          {field('document_date', 'Document Date', true,  'date')}
          {field('posting_date',  'Posting Date',  true,  'date')}
          {field('document_type', 'Document Type', true,  'text', { maxLength: 2,  placeholder: 'SA / KR' })}
          {field('currency',      'Currency',      true,  'text', { maxLength: 3,  placeholder: 'INR / USD' })}
          {field('reference',     'Reference',     false, 'text', { maxLength: 16 })}
          {field('header_text',   'Header Text',   false, 'text', { maxLength: 25 })}
        </div>
      </div>

      {/* Line Items */}
      <div className="journal-panel journal-form-section">
        <div className="journal-panel-head">
          <h3>Line Items</h3>
          <button className="journal-btn small ghost" onClick={addLine}><FiPlus size={13} /> Add Line</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="line-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>#</th>
                <th style={{ width: 90 }}>Posting Key *<br /><small style={{ fontWeight: 400, textTransform: 'none' }}>40=Dr 50=Cr</small></th>
                <th style={{ width: 105 }}>GL Account *</th>
                <th style={{ width: 105 }}>Amount *</th>
                <th style={{ minWidth: 110 }}>Cost CTR{spinner}</th>
                <th style={{ minWidth: 110 }}>Profit CTR{spinner}</th>
                <th style={{ minWidth: 100 }}>WBS{spinner}</th>
                <th>Item Text</th>
                <th style={{ width: 32 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const ccErr = lineVal[`${l.id}.cost_center`]?.state === 'error'    ? lineVal[`${l.id}.cost_center`].msg    : '';
                const pcErr = lineVal[`${l.id}.profit_center`]?.state === 'error'  ? lineVal[`${l.id}.profit_center`].msg  : '';
                return (
                  <tr key={l.id}>
                    <td style={{ color: '#a0a7c0', fontSize: 11, textAlign: 'center' }}>{idx + 1}</td>

                    <td>
                      <select value={l.posting_key} onChange={e => setL(l.id, 'posting_key', e.target.value)}
                        className={lErr[`${l.id}.posting_key`] ? 'err' : ''}>
                        <option value="">—</option>
                        <option value="40">40 (Dr)</option>
                        <option value="50">50 (Cr)</option>
                      </select>
                    </td>

                    <td>
                      <input value={l.gl_account} maxLength={10}
                        onChange={e => setL(l.id, 'gl_account', e.target.value)}
                        className={lErr[`${l.id}.gl_account`] ? 'err' : ''} />
                    </td>

                    <td>
                      <input type="number" min="0.01" step="0.01" value={l.amount}
                        onChange={e => setL(l.id, 'amount', e.target.value)}
                        className={lErr[`${l.id}.amount`] ? 'err' : ''} />
                    </td>

                    {/* Cost CTR */}
                    <td>
                      <input list={`cc-${l.id}`} value={l.cost_center} maxLength={10}
                        placeholder={masterLoading ? 'Loading…' : ''}
                        onChange={e => handleCCChange(l.id, e.target.value)}
                        onBlur={() => handleCCBlur(l.id, l.cost_center)}
                        style={{ borderColor: ccBorder(l.id) }} />
                      <datalist id={`cc-${l.id}`}>
                        {costCenters.map(c => <option key={c.cost_center} value={c.cost_center} />)}
                      </datalist>
                      {ccErr && <div style={{ fontSize: '0.68rem', color: '#ef4444', marginTop: 2 }}>{ccErr}</div>}
                    </td>

                    {/* Profit CTR */}
                    <td>
                      <input list={`pc-${l.id}`} value={l.profit_center} maxLength={10}
                        placeholder={masterLoading ? 'Loading…' : ''}
                        onChange={e => handlePCChange(l.id, e.target.value)}
                        onBlur={() => handlePCBlur(l.id, l.profit_center)}
                        style={{ borderColor: pcBorder(l.id) }} />
                      <datalist id={`pc-${l.id}`}>
                        {profitCenters.map(p => <option key={p.profit_center} value={p.profit_center} />)}
                      </datalist>
                      {pcErr && <div style={{ fontSize: '0.68rem', color: '#ef4444', marginTop: 2 }}>{pcErr}</div>}
                    </td>

                    {/* WBS */}
                    <td>
                      <input list={`wbs-${l.id}`} value={l.wbs_element} maxLength={24}
                        disabled={wbsDisabled}
                        placeholder={wbsDisabled ? 'N/A' : masterLoading ? 'Loading…' : ''}
                        onChange={e => setL(l.id, 'wbs_element', e.target.value)}
                        style={wbsDisabled ? { background: '#f5f7fa', color: '#a0a7c0', cursor: 'not-allowed' } : undefined} />
                      {!wbsDisabled && (
                        <datalist id={`wbs-${l.id}`}>
                          {wbsElements.map(w => <option key={w.wbs_element} value={w.wbs_element}>{w.description}</option>)}
                        </datalist>
                      )}
                    </td>

                    <td>
                      <input value={l.item_text} maxLength={50} onChange={e => setL(l.id, 'item_text', e.target.value)} />
                    </td>

                    <td>
                      <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px', display: 'flex' }}
                        onClick={() => removeLine(l.id)}><FiTrash2 size={13} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="balance-bar">
          <div className="b-item">Debits (40)<strong>{debits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
          <div className="b-item">Credits (50)<strong>{credits.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
          <div className="b-item">Diff<strong>{Math.abs(debits - credits).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></div>
          <div className={`b-status ${balanced ? 'ok' : 'bad'}`}>{balanced ? '✓ Balanced' : '✗ Unbalanced'}</div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button className="journal-btn ghost" onClick={() => onNavigate('entries')}>Cancel</button>
        <button className="journal-btn primary" onClick={handleSubmit}
          disabled={submitting || !balanced || hasValErrors}
          title={hasValErrors ? 'Fix cost/profit centre errors first' : undefined}>
          {submitting ? 'Submitting…' : 'Submit Entry'}
        </button>
      </div>
    </div>
  );
}
